/* eslint-disable unused-imports/no-unused-vars */
'use client'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { ChangeEvent, Suspense, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { titleCaseContainsSatdik } from '@/utils/string'
import { JENJANG_SD } from '@/apis/data/statistik/jenjang'
import { MAP_STATUS_BY_VALUE } from '@/apis/data/statistik/status'
import {
  District,
  DistrictResponse,
  EducationLevel,
  EducationStatus,
  FilterResponse,
  StatisticDetailResponse,
  StatistikDetailServerParams,
  TableMappingResponse,
} from '@/apis/types'

import { DownloadSurveyProvider } from '@/components/DownloadSurvey/DownloadSurveyProvider'
import { EmptyState } from '@/components/EmptyState'
import { RoundedTableLoader } from '@/components/RoundedTable/RoundedTableLoader'

import { HeaderStatistik } from './HeaderStatistik'
import { TableDetail } from './TableStatistik/TableDetail'
import { FilterEducationLevel } from '../FilterSelect/FilterEducationLevel'
import { FilterSection } from '../FilterSelect/FilterSection'
import { FilterStatus } from '../FilterSelect/FilterStatus'
import { FilterWilayah } from '../FilterSelect/FilterWilayah'
import { FilterYearPeriod } from '../FilterSelect/FilterYearPeriod'

const ButtonDownload = dynamic(
  () =>
    import('./ButtonDownload/ButtonDownload').then((mod) => mod.ButtonDownload),
  {
    ssr: false,
  }
)

type ContentDetailProps = {
  dataDetail: StatisticDetailResponse
  dataFilters: FilterResponse
  dataDistricts: DistrictResponse
  dataTableMappings?: TableMappingResponse
  params: StatistikDetailServerParams
}

function FallbackError() {
  return (
    <EmptyState
      title="Oops, data tidak ditemukan"
      description="Laman data statistik yang Anda tuju tidak dapat ditemukan"
    />
  )
}

export const ContentDetail = ({
  dataDetail,
  dataFilters,
  dataDistricts,
  dataTableMappings,
  params,
}: ContentDetailProps) => {
  const router = useRouter()

  const [selectedWilayah, setSelectedWilayah] = useState<District>(
    params.district
  )
  const [selectedStatus, setSelectedStatus] = useState<EducationStatus>(
    params.statusCode
  )

  const [selectedJenjang, setSelectedJenjang] = useState<EducationLevel>({
    id: parseInt(params.educationLevelId, 10),
    name: params.educationLevelName,
  })

  const [selectedTahunAjaran, setSelectedTahunAjaran] = useState<number>(
    parseInt(params.year, 10)
  )

  const handleChangeJenjang = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    const match = dataFilters?.educationLevel?.find(
      (item: EducationLevel) => `${item.id}` === newValue
    )
    if (match) {
      setSelectedJenjang(match)

      const withStatusQuery =
        params.status !== 'semua'
          ? `?status=${params.status?.toLowerCase()}`
          : ''

      const matchedTableMapping = (dataTableMappings?.tableMappings || []).find(
        (mapping) => mapping?.educationLevel?.id === match.id
      )

      if (matchedTableMapping) {
        router.replace(
          `/dataset/detail/${matchedTableMapping.tableNum}/L${selectedWilayah.districtLevel}-${selectedWilayah.id}/${params.year}/${match.name}-${match.id}${withStatusQuery}#filter-section`
        )
      } else {
        router.replace(
          `/dataset/detail/${params.tableNum}/L${selectedWilayah.districtLevel}-${selectedWilayah.id}/${params.year}/${match.name}-${match.id}${withStatusQuery}#filter-section`
        )
      }
    }
  }

  const handleChangeStatus = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value as EducationStatus
    setSelectedStatus(newValue)

    const withStatusQuery =
      newValue !== 'EDUCATION_STATUS_UNSPECIFIED'
        ? `?status=${MAP_STATUS_BY_VALUE[newValue]?.toLowerCase()}`
        : ''

    router.replace(
      `/dataset/detail/${params.tableNum}/L${selectedWilayah.districtLevel}-${selectedWilayah.id}/${params.year}/${params.educationLevelName}-${params.educationLevelId}${withStatusQuery}#filter-section`
    )
  }

  const handleChangeTahunAjaran = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    const newValueInt = parseInt(newValue, 10)
    setSelectedTahunAjaran(newValueInt)

    const withStatusQuery =
      params.status !== 'semua' ? `?status=${params.status?.toLowerCase()}` : ''

    router.replace(
      `/dataset/detail/${params.tableNum}/L${selectedWilayah.districtLevel}-${selectedWilayah.id}/${newValueInt}/${params.educationLevelName}-${params.educationLevelId}${withStatusQuery}#filter-section`
    )
  }

  const handleChangeWilayah = (newWilayah: District) => {
    setSelectedWilayah(newWilayah)
    const withStatusQuery =
      params.status !== 'semua' ? `?status=${params.status?.toLowerCase()}` : ''

    // Adding timeout here to give a time for the dialog to be closed first
    setTimeout(() => {
      router.replace(
        `/dataset/detail/${params.tableNum}/L${newWilayah.districtLevel}-${newWilayah.id}/${params.year}/${params.educationLevelName}-${params.educationLevelId}${withStatusQuery}#filter-section`
      )
    }, 500)
  }

  // Select default filters
  useEffect(() => {
    if (dataFilters) {
      if (params.statusCode) {
        setSelectedStatus(params.statusCode)
      } else {
        setSelectedStatus(
          (dataFilters?.educationStatus?.[0] as EducationStatus) ||
            'EDUCATION_STATUS_UNSPECIFIED'
        )
      }

      setSelectedTahunAjaran(
        dataFilters?.yearPeriod?.find((year) => `${year}` === params.year) || 0
      )

      const matchJenjang = dataFilters?.educationLevel?.find(
        (item: EducationLevel) =>
          `${item.name}` === `${params.educationLevelName}`
      )

      setSelectedJenjang(matchJenjang || JENJANG_SD)
    }
  }, [dataFilters, params.educationLevelName, params.statusCode, params.year])

  const titleCaseTitle = titleCaseContainsSatdik(dataDetail.data.name)
  const withStatus =
    selectedStatus !== 'EDUCATION_STATUS_UNSPECIFIED'
      ? ` ${MAP_STATUS_BY_VALUE[selectedStatus || '']?.toLowerCase()}`
      : ''

  const titleAndTagCaseTitle = `${titleCaseTitle} (${selectedWilayah.name}) Tahun ${selectedTahunAjaran}/${
    selectedTahunAjaran + 1
  } ${selectedJenjang.name}${titleCaseContainsSatdik(withStatus)}`

  return (
    <DownloadSurveyProvider initialSource="DATASET">
      <HeaderStatistik
        jenjang={selectedJenjang}
        district={selectedWilayah}
        year={selectedTahunAjaran}
        title={titleCaseTitle}
      />

      <FilterSection
        FilterWilayah={
          <FilterWilayah
            data={dataDistricts}
            loadingStatus={'success'}
            value={selectedWilayah}
            onChange={handleChangeWilayah}
          />
        }
        FilterYearPeriod={
          <FilterYearPeriod
            yearPeriod={dataFilters?.yearPeriod}
            loadingStatus={'success'}
            value={selectedTahunAjaran}
            onChange={handleChangeTahunAjaran}
          />
        }
        FilterEducationLevel={
          <FilterEducationLevel
            educationLevel={dataFilters?.educationLevel}
            loadingStatus={'success'}
            value={selectedJenjang}
            onChange={handleChangeJenjang}
          />
        }
        FilterStatus={
          dataFilters && dataFilters?.educationStatus?.length > 0 ? (
            <FilterStatus
              educationStatus={dataFilters?.educationStatus}
              loadingStatus={'success'}
              value={selectedStatus}
              onChange={handleChangeStatus}
            />
          ) : null
        }
        ButtonApply={null}
        ButtonDownload={
          /*
          // @ts-ignore */
          <ButtonDownload
            tableId={params.tableNum}
            jenjang={selectedJenjang}
            district={selectedWilayah}
            year={selectedTahunAjaran}
            status={selectedStatus}
            disabled={false}
            tableType={dataDetail?.data?.tableType}
            titleWithTag={titleAndTagCaseTitle}
          />
        }
      />

      <Suspense fallback={<RoundedTableLoader rows={10} />}>
        <ErrorBoundary FallbackComponent={FallbackError}>
          <TableDetail
            dataDetail={dataDetail}
            districts={dataDistricts?.data || []}
            tableNum={params.tableNum}
            districtId={selectedWilayah.id}
            districtName={selectedWilayah.name}
            districtLevel={selectedWilayah.districtLevel}
            yearPeriod={selectedTahunAjaran}
            educationLevelId={selectedJenjang.id}
            educationLevelName={selectedJenjang.name}
            educationStatus={selectedStatus}
            title={titleCaseTitle}
            titleWithTag={titleAndTagCaseTitle}
          />
        </ErrorBoundary>
      </Suspense>
    </DownloadSurveyProvider>
  )
}
